import { ToolCallDisplay, ToolCardKind } from '../types/chat';
import { ToolStreamEvent } from '../services/api/streamingService';

/**
 * SSE の tool_use / tool_result イベントを、メッセージの tool_calls 配列へ
 * call_id 単位で upsert する（ストリーミング中のカード状態を組み立てる）。
 *
 * - tool_use(running)   → カード作成/更新（実行中）
 * - tool_use(progress)  → progress% と進捗メッセージを更新
 * - tool_result         → completed / error に確定し summary・job_id・images を反映
 */
export function mergeToolEvent(
  existing: ToolCallDisplay[] | undefined,
  event: ToolStreamEvent
): ToolCallDisplay[] {
  const list = existing ? [...existing] : [];
  const index = list.findIndex((c) => c.call_id === event.call_id);
  const current: ToolCallDisplay = index >= 0
    ? { ...list[index] }
    : {
        call_id: event.call_id,
        name: event.name,
        label: event.label || event.name,
        status: 'running',
      };

  if (event.type === 'tool_use') {
    if (event.label) {
      current.label = event.label;
    }
    if (event.status === 'progress') {
      current.status = 'progress';
      if (event.progress !== undefined && event.progress !== null) {
        current.progress = event.progress;
      }
      if (event.args_summary) {
        current.args_summary = event.args_summary;
      }
    } else {
      current.status = 'running';
      if (event.args_summary) {
        current.args_summary = event.args_summary;
      }
    }
  } else {
    // tool_result
    current.status = event.ok ? 'completed' : 'error';
    if (event.summary !== undefined) {
      current.summary = event.summary;
    }
    if (event.job_id !== undefined) {
      current.job_id = event.job_id;
    }
    if (event.images && event.images.length > 0) {
      current.images = event.images;
    }
    if (event.kind) {
      current.kind = event.kind;
    }
    if (event.videos && event.videos.length > 0) {
      current.videos = event.videos;
    }
    if (event.jobs && event.jobs.length > 0) {
      current.jobs = event.jobs;
    }
    if (event.proposal) {
      current.proposal = event.proposal;
    }
    if (event.chips && event.chips.length > 0) {
      current.chips = event.chips;
    }
    if (event.proposal_code) {
      current.proposal_code = event.proposal_code;
    }
    if (event.choices) {
      current.choices = event.choices;
    }
    if (event.block) {
      current.block = event.block;
    }
    if (!current.kind) {
      current.kind = resolveToolCardKind(current);
    }
  }

  if (index >= 0) {
    list[index] = current;
  } else {
    list.push(current);
  }

  return list;
}

/**
 * サーバー永続化レコード（messages.tool_calls）を表示用の形に正規化する。
 *
 * 永続化レコードは status / label を持たず ok(boolean) と name のみを持つため、
 * そのまま描画すると「無限スピナー・ラベル空」になる。履歴再表示（リロード）時は
 * 必ずこの関数を通してから ToolActivityCard に渡す。
 */
const TOOL_LABELS: Record<string, string> = {
  register_product_from_url: '商品URL登録',
  get_product_fetch_progress: '商品取得進捗の確認',
  list_products: '商品一覧の取得',
  get_product: '商品詳細の取得',
  list_image_styles: 'バナースタイル一覧',
  generate_banner: 'バナー生成',
  get_image_job: '画像生成ジョブの確認',
  list_organization_banners: '組織バナーライブラリ一覧',
  generate_video: '動画生成',
  get_video_job: '動画の状態確認',
  list_videos: '動画一覧',
  make_banner_video: 'バナーの動画化',
  generate_carousel: 'カルーセル生成',
  chat_image: '画像生成',
  chat_video: '動画生成',
  ask_user_choices: '選択肢',
};

export function normalizeToolCall(card: Partial<ToolCallDisplay> & { call_id: string; name: string }): ToolCallDisplay {
  return {
    ...card,
    call_id: card.call_id,
    name: card.name,
    label: card.label || TOOL_LABELS[card.name] || card.name,
    // 永続化レコードは常に実行済み。ok === false のみエラー扱い。
    status: card.status ?? (card.ok === false ? 'error' : 'completed'),
    kind: card.kind ?? resolveToolCardKind(card),
  };
}

/** kind 未設定のレコード（V1 の永続化データ）から表示種別を推定する。 */
export function resolveToolCardKind(card: Partial<ToolCallDisplay>): ToolCardKind {
  if (card.kind) return card.kind;
  if (card.choices) return 'choices';
  if (card.proposal) return 'proposal';
  if (card.videos && card.videos.length > 0) return 'video';
  if (card.jobs && card.jobs.length > 0) return 'carousel';
  if (card.images && card.images.length > 0) return 'image';
  return 'info';
}

/** 提案カードの「この内容で生成する」が送るユーザーメッセージ。サーバーは本文にコードが含まれることを検証する。 */
export const CONFIRM_MESSAGE = (code: string): string => `この内容で生成してください（確認コード: ${code}）`;

/** 失敗カードの「同じ設定でもう一度」が送るユーザーメッセージ。 */
export const RETRY_MESSAGE = '同じ設定でもう一度生成して';
